// src/components/cctv/CCTVList.js
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Video, RefreshCcw } from 'lucide-react';

import { useAtom } from 'jotai';
import { activeChildDetail, appConstants, cctvIntial, tab } from '../../atomstore/store';
import { Capacitor } from '@capacitor/core';

import {
    checkCCTVConnectionFunction,
    ReloadData,
    fetchEmbedHash,
    exchangeCctvHybridAppAuth,
} from "../../queryfunctions/queryfunctions";

import { useRequestProcessor } from "../../requestProcessor";



import CameraView from './CameraView';
import { getCctvPlayerMode } from './cctvPlayerMode';
import { useNavigate } from 'react-router-dom';


const CCTVList = () => {
    const navigate = useNavigate()
    const [, setActiveTab] = useAtom(tab)
    const [cctvState, setCctvState] = useAtom(cctvIntial);
    const [activeChild] = useAtom(activeChildDetail);
    const [runtimeConstants] = useAtom(appConstants);

    const [streamError, setStreamError] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [childOnPause, setChildOnPause] = useState(false);

    const { useMutationWrapper, useQueryWrapper } = useRequestProcessor();

    // Derive playback mode purely from the stream URL:
    //   legacy      → fp.streamonweb.com / chennaistream.net (old token path)
    //   embed_test  → test.streamonweb.com (POST-embed + hash, no live flag)
    //   embed_live  → aivmsfp.streamonweb.com (POST-embed + hash with live: true)
    const streamLink = activeChild?.html5_player_link || "";
    const cctvPlayerMode = getCctvPlayerMode(streamLink);
    const useLegacyCctvPlayback = cctvPlayerMode === "legacy";
    const useEmbedStylePlayback =
        cctvPlayerMode === "embed_test" || cctvPlayerMode === "embed_live";
    const embedHashLive = cctvPlayerMode === "embed_live";

    /* ---------------------------------
       TOKEN VALIDATION
    -------------------------------- */
    const getValidToken = (tokens = []) => {
        const nowUTC = moment.utc();
        return tokens.find(({ start, end }) => {
            const s = moment.utc(start);
            const e = moment.utc(end);
            return nowUTC.isBetween(s, e);
        })?.token;
    };

    /* ---------------------------------
       HASH FOR EMBEDDED CCTV (MD5)
       Used for StreamOnWeb-style POST-based embeds
    -------------------------------- */
    // Hybrid auth only applies inside the native Capacitor runtime (not browser).
    const isHybridApp = useEmbedStylePlayback && Capacitor?.isNativePlatform?.() === true;
    const streamUrlObj = (() => {
        try {
            return streamLink ? new URL(streamLink) : null;
        } catch {
            return null;
        }
    })();
    const streamOrigin = streamUrlObj?.origin || null;
    const centerSlug = (() => {
        if (!streamUrlObj) return null;
        const segments = streamUrlObj.pathname.split("/").filter(Boolean);
        if (!segments || segments.length < 2) return null;
        if (segments[0] === "center" || segments[0] === "c") return segments[1];
        return null;
    })();
    const hybridCenterPageUrl =
        useEmbedStylePlayback && streamOrigin && centerSlug
            ? `${streamOrigin}/center/${centerSlug}`
            : null;
    const hybridAppAuthUrl =
        hybridCenterPageUrl ? `${hybridCenterPageUrl}/app-auth` : null;

    const hybridAuthEnabled =
        isHybridApp &&
        !!hybridAppAuthUrl &&
        !!hybridCenterPageUrl;

    const hybridAuthQuery = useQueryWrapper(
        ["cctv-hybrid-auth", hybridAppAuthUrl, embedHashLive],
        () => exchangeCctvHybridAppAuth({ appAuthUrl: hybridAppAuthUrl, live: embedHashLive }),
        {
            enabled: hybridAuthEnabled,
            retry: 0,
        }
    );

    const hybridSessionToken = hybridAuthQuery?.data?.app_session_token || null;
    const hybridSrc =
        hybridAuthEnabled && hybridCenterPageUrl
            ? (hybridSessionToken
                ? `${hybridCenterPageUrl}?app_session=${encodeURIComponent(hybridSessionToken)}`
                : hybridCenterPageUrl)
            : null;

    const { data: embedHash } = useQueryWrapper(
        ["cctv-embed-hash", activeChild?.id, activeChild?.center_id, embedHashLive],
        () => fetchEmbedHash({ live: embedHashLive }),
        {
            enabled: useEmbedStylePlayback && !!activeChild && !hybridAuthEnabled,
        }
    );

    /* ---------------------------------
       MUTATIONS
    -------------------------------- */
    const checkConnection = useMutationWrapper("checkCctv", () =>
        checkCCTVConnectionFunction(cctvState, setCctvState, activeChild)
    );

    const reloadToken = useMutationWrapper("reload", () =>
        ReloadData().then(() => {
            setStreamError(false);
            setRefreshKey(k => k + 1);
            checkConnection.mutate();
        })
    );

    /* ---------------------------------
       INITIAL LOAD
    -------------------------------- */
    useEffect(() => {
        if (!activeChild) return;

        setChildOnPause(activeChild.pause_state === 1);

        if (useLegacyCctvPlayback) {
            // Legacy: always need a valid access token for the stream URL.
            const token = getValidToken(activeChild?.cctv_access_key?.token);
            if (!token) setStreamError(true);
        }
        // embed_test / embed_live: hash-based embed, no access token needed.

        checkConnection.mutate();
        // We intentionally do NOT include dependencies here beyond initial mount,
        // because downstream CCTV APIs and state assume this runs only once per screen load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------------------------
       ALLOWED HOURS CHECK
    -------------------------------- */
    const isWithinAllowedHours = () => {
        const nowUTC = moment.utc();
        const startUTC = moment.utc().startOf("day").add(2, "hours").add(30, "minutes"); // 8 AM IST
        const endUTC = moment.utc().startOf("day").add(14, "hours").add(30, "minutes"); // 8 PM IST
        return nowUTC.isBetween(startUTC, endUTC);
    };

    /* ---------------------------------
      BUILD FINAL STREAM URL (for direct streams)
      - Legacy direct links continue to use token-based URLs
      - StreamOnWeb-style centers use POST-based iframe embed instead,
        so we do NOT build a direct stream URL for them.
    -------------------------------- */
    const buildStreamUrl = () => {
        // Embed modes use POST-based iframe; no direct stream URL needed.
        if (useEmbedStylePlayback) return null;

        const authToken = getValidToken(activeChild?.cctv_access_key?.token);
        if (!authToken) return null;

        return `${activeChild?.html5_player_link}/${activeChild?.center_id}/${activeChild?.id}/${authToken}`;
    };

    const streamUrl = buildStreamUrl();

    const usePostEmbed =
        useEmbedStylePlayback &&
        !!activeChild &&
        !hybridAuthEnabled &&
        !!activeChild?.html5_player_link &&
        !!embedHash?.id1 &&
        !!embedHash?.id2 &&
        !!embedHash?.hash;

    const embedUrl = usePostEmbed ? activeChild?.html5_player_link : null;

    /* ---------------------------------
       LIST UI WITH STATE CHECKS
    -------------------------------- */
    const renderBlock = (title, subtitle, action = null) => (
        <div className="p-6  text-center my-4">
            <Video className="w-12 h-12 text-gray-600 mx-auto mb-4 opacity-40" />

            <p className="font-semibold text-lg">{title}</p>
            {subtitle && <p className="text-gray-400 text-sm mt-2">{subtitle}</p>}

            {action && (
                <button
                    onClick={action.onClick}
                    className="px-5 py-2 mt-4 bg-orange-500 rounded-lg flex items-center space-x-2 mx-auto text-white"
                >
                    <RefreshCcw className="w-4 h-4" />
                    <span>{action.label}</span>
                </button>
            )}
        </div>
    );

    console.log("Constants at cctv", runtimeConstants)


    /* ---------------------------------
       CONDITIONS BEFORE DISPLAYING LIST
    -------------------------------- */

    if (childOnPause) {
        return renderBlock(
            runtimeConstants?.cameraPauseTitle,
            runtimeConstants?.cameraPauseSubtitle
        );
    }

    if (cctvState.centerCctvDown === true) {
        return renderBlock(
            "Internet at the center is not working",
            "We will notify you once the connection is restored.",
            {
                label: checkConnection.isLoading ? "Refreshing..." : "Refresh",
                onClick: () => checkConnection.mutate(),
            }
        );
    }

    if (!isWithinAllowedHours()) {
        return renderBlock(
            "Camera Offline",
            "Live stream is available only between 8:00 AM and 8:00 PM IST."
        );
    }

    if (streamError) {
        return renderBlock(
            "Livestream Not Available",
            "Please refresh to generate a new secure token.",
            {
                label: reloadToken.isLoading ? "Refreshing..." : "Refresh Token",
                onClick: () => reloadToken.mutate(),
            }
        );
    }



    return (
        <CameraView
            streamUrl={streamUrl}
            key={refreshKey}
            useLegacyPlayer={useLegacyCctvPlayback}
            usePostEmbed={usePostEmbed}
            embedUrl={embedUrl}
            embedParams={embedHash}
            hybridAuth={
                hybridAuthEnabled
                    ? {
                        enabled: true,
                        centerPageUrl: hybridCenterPageUrl,
                        src: hybridSrc,
                        isLoading: hybridAuthQuery?.isLoading === true,
                        hasError: hybridAuthQuery?.isError === true,
                    }
                    : { enabled: false }
            }
            onBack={() => {
                // Prefer an explicit route over navigate(-1): browser history and the
                // app's navStack can diverge (BottomNav / Home both push /cctv), so
                // the first -1 sometimes lands on another CCTV entry or a dead entry
                // and it feels like the button needs two taps.
                setActiveTab("home");
                navigate("/home");
            }}
        />
    );

    /* ---------------------------------
       MAIN CAMERA LIST
    -------------------------------- */
    // return (
    //     <div className="p-4 space-y-3">
    //         <h2 className="text-xl font-bold text-gray-800 mb-4">Live Camera Feed</h2>

    //         {(reloadToken.isLoading || checkConnection.isLoading) && (
    //             <SpinnerLoading />
    //         )}

    //         {cameras.map((camera) => (
    //             <button
    //                 key={camera.id}
    //                 onClick={() => {
    //                     setCurrentScreen('camera-view')
    //                     setCamera(streamUrl)
    //                 }}
    //                 className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:scale-95 transition"
    //             >
    //                 <div className="flex items-center justify-between">
    //                     <div className="flex items-center space-x-3">
    //                         <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
    //                             <Video className="w-6 h-6 text-white" />
    //                         </div>
    //                         <div className="text-left">
    //                             <p className="font-semibold text-gray-800">{camera.name}</p>
    //                             <p className="text-sm text-gray-500">{camera.location}</p>
    //                         </div>
    //                     </div>

    //                     <div className="flex items-center space-x-2">
    //                         <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
    //                         <span className="text-sm text-green-600 font-medium">Live</span>
    //                     </div>
    //                 </div>
    //             </button>
    //         ))}
    //     </div>
    // );
};

export default CCTVList;
